import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, Edit, Trash2, TrendingDown, Calendar, DollarSign, X } from 'lucide-react';
import { expensesApi } from '../services/api';
import { useToast } from '../hooks/use-toast';

const ExpenseManager = ({ onClose }) => {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    category: 'operating',
    status: 'active',
    isRecurring: false,
    recurringInterval: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: ''
  });

  const categories = {
    operating: 'Betriebskosten',
    marketing: 'Marketing',
    personnel: 'Personal',
    equipment: 'Ausrüstung',
    rent: 'Miete',
    utilities: 'Nebenkosten',
    insurance: 'Versicherung',
    software: 'Software/Lizenzen',
    other: 'Sonstiges'
  };

  const statusLabels = {
    planned: 'Geplant',
    active: 'Aktiv',
    paused: 'Pausiert',
    cancelled: 'Storniert'
  };

  const intervals = {
    monthly: 'Monatlich',
    quarterly: 'Vierteljährlich',
    yearly: 'Jährlich'
  };

  useEffect(() => {
    loadExpenses();
    loadSummary();
  }, []);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const response = await expensesApi.getAll('default_company');
      setExpenses(response.data || []);
    } catch (error) {
      console.error('Error loading expenses:', error);
      toast({
        title: "Fehler",
        description: "Ausgaben konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await expensesApi.getSummary('default_company');
      setSummary(response.data || {});
    } catch (error) {
      console.error('Error loading expense summary:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.amount || !formData.startDate) {
      toast({
        title: "Hinweis",
        description: "Bitte füllen Sie alle Pflichtfelder aus.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      
      const expenseData = {
        ...formData,
        amount: parseFloat(formData.amount),
        companyId: 'default_company',
        recurringInterval: formData.isRecurring ? formData.recurringInterval : null,
        endDate: formData.endDate || null
      };

      if (editingExpense) {
        await expensesApi.update(editingExpense.id, expenseData);
        toast({
          title: "Erfolg",
          description: "Ausgabe erfolgreich aktualisiert.",
        });
      } else {
        await expensesApi.create(expenseData);
        toast({
          title: "Erfolg",
          description: "Ausgabe erfolgreich angelegt.",
        });
      }
      
      resetForm();
      await loadExpenses();
      await loadSummary();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast({
        title: "Fehler",
        description: error?.response?.data?.detail || "Ausgabe konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      title: expense.title,
      description: expense.description || '',
      amount: expense.amount.toString(),
      category: expense.category,
      status: expense.status,
      isRecurring: expense.isRecurring,
      recurringInterval: expense.recurringInterval || '',
      startDate: expense.startDate,
      endDate: expense.endDate || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Möchten Sie diese Ausgabe wirklich löschen?')) {
      return;
    }

    try {
      setLoading(true);
      await expensesApi.delete(expenseId, 'default_company');
      toast({
        title: "Erfolg",
        description: "Ausgabe wurde gelöscht.",
      });
      await loadExpenses();
      await loadSummary();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast({
        title: "Fehler",
        description: "Ausgabe konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      amount: '',
      category: 'operating',
      status: 'active',
      isRecurring: false,
      recurringInterval: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: ''
    });
    setEditingExpense(null);
    setShowForm(false);
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <TrendingDown className="w-6 h-6 text-red-600" />
          <h2 className="text-2xl font-bold text-slate-800">Ausgaben verwalten</h2>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Neue Ausgabe
          </Button>
          {onClose && (
            <Button variant="outline" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">Monatlich</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {summary.monthlyExpenses?.toLocaleString('de-DE', { minimumFractionDigits: 2 }) || '0,00'}€
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">Vierteljährlich</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {summary.quarterlyExpenses?.toLocaleString('de-DE', { minimumFractionDigits: 2 }) || '0,00'}€
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">Jährlich</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {summary.yearlyExpenses?.toLocaleString('de-DE', { minimumFractionDigits: 2 }) || '0,00'}€
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">Aktive Ausgaben</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">
              {summary.activeCount || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expenses Table */}
      <Card>
        <CardHeader>
          <CardTitle>Alle Ausgaben</CardTitle>
        </CardHeader>
        <CardContent>
          {loading && expenses.length === 0 ? (
            <div className="text-center py-8 text-slate-500">Lade Ausgaben...</div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <TrendingDown className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Noch keine Ausgaben angelegt</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titel</TableHead>
                  <TableHead>Kategorie</TableHead>
                  <TableHead>Betrag</TableHead>
                  <TableHead>Turnus</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Startdatum</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{expense.title}</div>
                        {expense.description && (
                          <div className="text-xs text-slate-500">{expense.description}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{categories[expense.category]}</Badge>
                    </TableCell>
                    <TableCell className="font-semibold text-red-600">
                      {expense.amount.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                    </TableCell>
                    <TableCell>
                      {expense.isRecurring ? (
                        <Badge className="bg-orange-100 text-orange-800 border-orange-300">
                          {intervals[expense.recurringInterval]}
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-500">Einmalig</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        className={
                          expense.status === 'active' ? 'bg-green-100 text-green-800 border-green-300' :
                          expense.status === 'planned' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                          expense.status === 'paused' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                          'bg-slate-100 text-slate-800 border-slate-300'
                        }
                      >
                        {statusLabels[expense.status]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="w-3 h-3 text-slate-500" />
                        {new Date(expense.startDate).toLocaleDateString('de-DE')}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(expense)}>
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => handleDelete(expense.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingExpense ? 'Ausgabe bearbeiten' : 'Neue Ausgabe anlegen'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Titel *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="z.B. Büromiete"
                  required
                />
              </div>

              <div>
                <Label htmlFor="amount">Betrag *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0,00"
                  required
                />
              </div>

              <div>
                <Label htmlFor="category">Kategorie</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categories).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(statusLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="startDate">Startdatum *</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="endDate">Enddatum (optional)</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Zusätzliche Informationen..."
                rows={2}
              />
            </div>

            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded">
              <input
                type="checkbox"
                id="isRecurring"
                checked={formData.isRecurring}
                onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="isRecurring" className="cursor-pointer">
                Wiederkehrende Ausgabe
              </Label>
            </div>

            {formData.isRecurring && (
              <div>
                <Label htmlFor="recurringInterval">Turnus</Label>
                <Select 
                  value={formData.recurringInterval} 
                  onValueChange={(value) => setFormData({ ...formData, recurringInterval: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Wählen Sie einen Turnus" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(intervals).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={resetForm}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Speichere...' : (editingExpense ? 'Aktualisieren' : 'Anlegen')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpenseManager;
